import { Button, Checkbox, Disclosure } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  contacts,
  contactRoles,
  offices,
  type Contact,
  type ContactRole,
  type Office,
  type UndoMergeResponse,
} from "../../../api/addressBook.service";
import LoadingState from "../../../components/LoadingState";
import { PageHeader } from "../../../components/PageHeader";
import { ROUTES } from "../../../routes";
import { formatDateForApi } from "../../../utils/dateFormatter";
import styles from "../AddressBook.module.css";
import { WizardStepper } from "../components/WizardStepper";
import introStyles from "./WizardIntro.module.css";

type Step = "select" | "review" | "done";

const STEP_LABELS = ["Select Roles", "Review", "Done"];

function stepToIndex(step: Step): number {
  switch (step) {
    case "select":
      return 0;
    case "review":
      return 1;
    case "done":
      return 2;
  }
}

function buildStepperSteps(currentStep: Step) {
  const currentIndex = stepToIndex(currentStep);
  return STEP_LABELS.map((label, i) => ({
    label,
    status: (i < currentIndex ? "complete" : i === currentIndex ? "active" : "pending") as
      | "complete"
      | "active"
      | "pending",
  }));
}

export default function ContactDemergeWizard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("select");
  const [targetContact, setTargetContact] = useState<Contact | null>(null);
  const [sourceContact, setSourceContact] = useState<Contact | null>(null);
  const [mergedOn, setMergedOn] = useState<string | null>(null);
  const [roles, setRoles] = useState<ContactRole[]>([]);
  const [officesMap, setOfficesMap] = useState<Map<string, Office>>(new Map());
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
  const [transferredRoleIdSet, setTransferredRoleIdSet] = useState<Set<string>>(new Set());
  const [effectiveDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [mergeRelationshipId, setMergeRelationshipId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UndoMergeResponse | null>(null);

  useEffect(() => {
    if (!id) return;

    async function loadData() {
      setLoading(true);
      try {
        const [contactRes, rolesRes, historyRes] = await Promise.all([
          contacts.getById(id!),
          contactRoles.getAll({ contactId: id! }),
          contacts.getMergeHistory(id!),
        ]);

        setTargetContact(contactRes.data);
        setRoles(rolesRes.data);

        if (historyRes.merges.length > 0) {
          const merge = historyRes.merges[0];
          setSourceContact(merge.sourceContact);
          setMergeRelationshipId(merge.relationship.id as number);
          const createdAt = merge.relationship.createdAt;
          if (typeof createdAt === "string") {
            setMergedOn(createdAt);
          }

          const activeRoles = rolesRes.data.filter(r => r.endDate === null);
          const metadata = merge.relationship.metadata as {
            transferredRoleIds?: string[];
          } | null;
          const transferredIds = metadata?.transferredRoleIds ?? [];
          const tSet = new Set(transferredIds);
          setTransferredRoleIdSet(tSet);

          if (transferredIds.length > 0) {
            setSelectedRoleIds(new Set(activeRoles.filter(r => tSet.has(r.id)).map(r => r.id)));
          } else {
            setSelectedRoleIds(new Set(activeRoles.map(r => r.id)));
          }
        }

        const officeIds = [...new Set(rolesRes.data.map(r => r.officeId).filter(Boolean))];
        const officeResults = await Promise.allSettled(officeIds.map(oid => offices.getById(oid)));
        const map = new Map<string, Office>();
        officeIds.forEach((oid, i) => {
          const res = officeResults[i];
          if (res.status === "fulfilled") {
            map.set(oid, res.value.data);
          }
        });
        setOfficesMap(map);
      } catch {
        setError("Failed to load contact data");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [id]);

  const activeRoles = roles.filter(
    r => r.endDate === null && (transferredRoleIdSet.size === 0 || transferredRoleIdSet.has(r.id)),
  );

  function officeName(officeId: string): string {
    const office = officesMap.get(officeId);
    return office?.name ?? "Unknown office";
  }

  const canProceed =
    mergeRelationshipId !== null && (activeRoles.length === 0 || selectedRoleIds.size > 0);

  async function handleSubmit() {
    if (!targetContact) return;
    setSubmitting(true);
    setError(null);

    try {
      const response = await contacts.demerge(targetContact.id, {
        mergeRelationshipId: mergeRelationshipId!,
        roleIds: [...selectedRoleIds],
        effectiveDate: effectiveDate ? (formatDateForApi(effectiveDate) ?? null) : null,
      });
      setResult(response);
      setStep("done");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to undo merge";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.wizardWrapper}>
        <PageHeader title="Undo Merge" />
        <LoadingState message="Loading contact data..." />
      </div>
    );
  }

  if (!targetContact) {
    return (
      <div className={styles.wizardWrapper}>
        <PageHeader title="Undo Merge" />
        <div className={styles.formAlert}>Contact not found.</div>
      </div>
    );
  }

  if (!sourceContact) {
    return (
      <div className={styles.wizardWrapper}>
        <PageHeader title="Undo Merge" subtitle={targetContact.name} />
        <div className={styles.formAlert}>This contact has no merge history to undo.</div>
      </div>
    );
  }

  return (
    <div className={styles.wizardWrapper}>
      <PageHeader title="Undo Merge" subtitle={targetContact.name} />

      <WizardStepper steps={buildStepperSteps(step)} />

      <div className={styles.wizardContent}>
        {step === "select" && (
          <div className={introStyles.intro}>
            <p className={introStyles.oneLiner}>Restores a previously archived contact.</p>
            <Disclosure summary="How it works">
              <p className={introStyles.detail}>
                The selected roles are moved from this contact back to the restored one. Only works
                if this contact has merge history.
              </p>
            </Disclosure>
          </div>
        )}

        {step === "select" && (
          <SelectStep
            sourceContact={sourceContact}
            mergedOn={mergedOn}
            activeRoles={activeRoles}
            selectedRoleIds={selectedRoleIds}
            effectiveDate={effectiveDate}
            officeName={officeName}
          />
        )}

        {step === "review" && (
          <ReviewStep
            sourceContact={sourceContact}
            targetContact={targetContact}
            activeRoles={activeRoles}
            selectedRoleIds={selectedRoleIds}
            totalActiveRoleCount={roles.filter(r => r.endDate === null).length}
            officeName={officeName}
            error={error}
          />
        )}

        {step === "done" && result && (
          <DoneStep sourceContact={sourceContact} rolesMoved={result.rolesMoved} />
        )}
      </div>

      <div className={styles.wizardActions}>
        {step === "select" && (
          <>
            <Button variant="outlined" color="default" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <div className={styles.wizardActionsRight}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setStep("review")}
                disabled={!canProceed}
              >
                Next
              </Button>
            </div>
          </>
        )}

        {step === "review" && (
          <>
            <Button variant="outlined" color="default" onClick={() => setStep("select")}>
              Back
            </Button>
            <div className={styles.wizardActionsRight}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Reversing..." : "Confirm"}
              </Button>
            </div>
          </>
        )}

        {step === "done" && result && (
          <div className={styles.wizardActionsRight}>
            <Button
              variant="outlined"
              color="default"
              onClick={() => navigate(ROUTES.addressBook.contacts)}
            >
              Back to Contacts
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(ROUTES.addressBook.contactDetail(result.restoredContact.id))}
            >
              View {sourceContact.name}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

type SelectStepProps = {
  sourceContact: Contact;
  mergedOn: string | null;
  activeRoles: ContactRole[];
  selectedRoleIds: Set<string>;
  effectiveDate: string;
  officeName: (officeId: string) => string;
};

function SelectStep({
  sourceContact,
  mergedOn,
  activeRoles,
  selectedRoleIds,
  effectiveDate,
  officeName,
}: SelectStepProps) {
  const selectedCount = selectedRoleIds.size;

  return (
    <div className={styles.wizardFormContainer}>
      <div className={`${styles.section} ${styles.sectionAccent}`}>
        <h3 className={styles.sectionTitle}>Original Contact</h3>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Name</span>
            <span className={styles.infoValue}>{sourceContact.name}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Email</span>
            <span className={styles.infoValue}>
              {sourceContact.email || <span className={styles.emptyText}>No email</span>}
            </span>
          </div>
          {mergedOn && (
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Merged on</span>
              <span className={styles.infoValue}>{new Date(mergedOn).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Roles to Return</h3>
        {selectedCount > 0 && (
          <div className={styles.selectionSummary}>
            {selectedCount} role{selectedCount !== 1 ? "s" : ""} selected
          </div>
        )}
        {activeRoles.length === 0 ? (
          <p className={styles.helperText}>No active roles found.</p>
        ) : (
          <div className={`${styles.roleCheckboxList} ${styles.roleCheckboxListSpaced}`}>
            {activeRoles.map(role => (
              <div
                key={role.id}
                className={`${styles.roleCheckboxItem} ${styles.roleCheckboxItemSelected} ${styles.roleCheckboxItemDisabled}`}
              >
                <Checkbox
                  checked
                  disabled
                  label={`${role.roleType || "Role"} \u2014 ${officeName(role.officeId)}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.section} style={{ marginTop: "var(--spacing-4)" }}>
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="undo-merge-effective-date">
            Effective Date
          </label>
          <input
            id="undo-merge-effective-date"
            type="date"
            className={styles.formInput}
            value={effectiveDate}
            disabled
          />
          <span className={styles.helperText}>Date the undo is recorded</span>
        </div>
      </div>
    </div>
  );
}

type ReviewStepProps = {
  sourceContact: Contact;
  targetContact: Contact;
  activeRoles: ContactRole[];
  selectedRoleIds: Set<string>;
  totalActiveRoleCount: number;
  officeName: (officeId: string) => string;
  error: string | null;
};

function ReviewStep({
  sourceContact,
  targetContact,
  activeRoles,
  selectedRoleIds,
  totalActiveRoleCount,
  officeName,
  error,
}: ReviewStepProps) {
  const selectedRoles = activeRoles.filter(r => selectedRoleIds.has(r.id));
  const remainingCount = totalActiveRoleCount - selectedRoles.length;

  return (
    <div className={styles.wizardFormContainer}>
      {error && <div className={styles.formAlert}>{error}</div>}

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Returning to: {sourceContact.name}</h3>
        <div className={styles.roleListReview}>
          {selectedRoles.map(role => (
            <div key={role.id} className={styles.roleItemReview}>
              {role.roleType || "Role"} — {officeName(role.officeId)}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.summaryRow}>
          <span>Roles staying with {targetContact.name}</span>
          <span
            className={`${styles.summaryBadge} ${remainingCount === 0 ? styles.summaryBadgeZero : ""}`}
          >
            {remainingCount}
          </span>
        </div>
      </div>

      <div className={styles.warningBox}>
        <strong>Warning:</strong> Selected roles will return to{" "}
        <strong>{sourceContact.name}</strong>. {sourceContact.name} will be restored.{" "}
        <strong>{targetContact.name}</strong> stays active.
      </div>
    </div>
  );
}

type DoneStepProps = {
  sourceContact: Contact;
  rolesMoved: number;
};

function DoneStep({ sourceContact, rolesMoved }: DoneStepProps) {
  return (
    <div className={styles.doneContainer}>
      <div className={styles.doneIconWrapper}>
        <span className={styles.doneIcon}>✓</span>
      </div>
      <h3 className={styles.doneTitle}>Merge Reversed</h3>
      <p className={styles.doneMessage}>
        {rolesMoved} role{rolesMoved !== 1 ? "s" : ""} returned to{" "}
        <strong>{sourceContact.name}</strong>
      </p>
    </div>
  );
}
